import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Cloud, RefreshCw, ShieldCheck, CheckCircle2, AlertCircle, Clock, Database,
    ArrowUpRight, History,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

function fmtRelative(iso) {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.floor(diff / 60000);
    if (min < 60) return `${min} min ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} h ago`;
    const d = Math.floor(h / 24);
    return `${d} d ago`;
}

export default function SharePointSync() {
    const [status, setStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const load = () =>
        api.get("/edi/sharepoint/status")
            .then((r) => setStatus(r.data))
            .catch(() => toast.error("Failed to load SharePoint status"));

    useEffect(() => { load(); }, []);

    const runSync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/edi/sharepoint/sync", {});
            setStatus(data);
            toast.success("SharePoint synchronisation triggered");
        } catch {
            toast.error("Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    if (!status) return <div className="kicker text-slate-500">Loading SharePoint status…</div>;

    return (
        <div className="space-y-6" data-testid={EDI.sharepointRoot}>
            {/* Connection status */}
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(59,130,246,0.20), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.14), transparent 55%)",
                    }}
                />
                <div className="relative flex items-start gap-4 flex-wrap">
                    <div className="h-12 w-12 rounded-md border border-blue-500/40 bg-blue-500/10 grid place-items-center">
                        <Cloud className="h-6 w-6 text-blue-400" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="kicker text-[var(--sgsb-gold)] text-[10px]">Microsoft 365 · SharePoint</span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[10px] text-emerald-400 font-display font-bold">
                                <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Connected
                            </span>
                        </div>
                        <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">
                            Live SharePoint Integration™
                        </h3>
                        <p className="text-slate-400 text-sm font-body mt-1 max-w-3xl">
                            Continue using SharePoint. EPCM360 quietly transforms every document into
                            searchable engineering intelligence — versioning, metadata, folder hierarchy
                            and permissions all preserved.
                        </p>
                        <div className="mt-3 text-[11px] text-slate-500 font-body">
                            Tenant: <span className="text-slate-300">sgsbateman.sharepoint.com</span>
                            &nbsp;·&nbsp; App: <span className="text-slate-300">EPCM360 Enterprise Connector</span>
                            &nbsp;·&nbsp; Auth: <span className="text-slate-300">OAuth 2.0 (delegated + application)</span>
                        </div>
                    </div>
                    <button
                        data-testid={EDI.sharepointSync}
                        onClick={runSync}
                        disabled={syncing}
                        className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} strokeWidth={2} />
                        {syncing ? "Syncing…" : "Trigger sync now"}
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["Sites Monitored",    status.sites_monitored,                              Cloud],
                    ["Documents Indexed",  status.documents_indexed.toLocaleString(),          Database],
                    ["Last Sync",          fmtRelative(status.last_sync),                       Clock],
                    ["Auto-Classified",    `${status.auto_classified_pct}%`,                    CheckCircle2],
                ].map(([k, v, Icon]) => (
                    <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{k}</div>
                        <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                    </div>
                ))}
            </div>

            {/* Sites monitored */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                    <Cloud className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <h3 className="font-display font-black text-white tracking-tight">Sites Monitored</h3>
                </div>
                <table className="w-full text-sm font-body">
                    <thead>
                        <tr className="text-slate-500 kicker text-[10px]">
                            <th className="text-left px-4 py-3">Site</th>
                            <th className="text-left px-4 py-3">Owner</th>
                            <th className="text-right px-4 py-3">Docs</th>
                            <th className="text-right px-4 py-3">Versions</th>
                            <th className="text-right px-4 py-3">Last Sync</th>
                            <th className="text-left px-4 py-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {status.sites.map((s) => (
                            <tr key={s.site} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                <td className="px-4 py-2.5 text-slate-100">
                                    <div className="flex items-center gap-1.5">
                                        <ArrowUpRight className="h-3 w-3 text-slate-500" strokeWidth={2} />
                                        {s.site}
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-slate-300">{s.owner}</td>
                                <td className="px-4 py-2.5 text-right text-slate-200">{s.docs.toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-right text-slate-400">{s.versions.toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-right text-slate-400">{fmtRelative(s.last_sync)}</td>
                                <td className="px-4 py-2.5">
                                    <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400">
                                        <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
                                        {s.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Sync history */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                    <History className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <h3 className="font-display font-black text-white tracking-tight">Synchronisation History</h3>
                </div>
                <table className="w-full text-sm font-body">
                    <thead>
                        <tr className="text-slate-500 kicker text-[10px]">
                            <th className="text-left px-4 py-3">Timestamp</th>
                            <th className="text-left px-4 py-3">Mode</th>
                            <th className="text-right px-4 py-3">New</th>
                            <th className="text-right px-4 py-3">Updated</th>
                            <th className="text-right px-4 py-3">Duplicates</th>
                            <th className="text-right px-4 py-3">Errors</th>
                            <th className="text-right px-4 py-3">Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {status.history.map((h, i) => (
                            <tr key={i} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                <td className="px-4 py-2.5 text-slate-100">{new Date(h.at).toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-slate-300">{h.mode}</td>
                                <td className="px-4 py-2.5 text-right text-emerald-400 font-display font-bold">+{h.new}</td>
                                <td className="px-4 py-2.5 text-right text-slate-300">{h.updated}</td>
                                <td className="px-4 py-2.5 text-right text-slate-400">{h.duplicates}</td>
                                <td className="px-4 py-2.5 text-right">
                                    {h.errors > 0
                                        ? <span className="text-red-400 inline-flex items-center gap-1 justify-end"><AlertCircle className="h-3 w-3" strokeWidth={2} />{h.errors}</span>
                                        : <span className="text-slate-500">0</span>}
                                </td>
                                <td className="px-4 py-2.5 text-right text-slate-400">{h.duration_s}s</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
